import React, { useMemo } from 'react';
import { Lottie } from 'lottie-react';
import batAnimation from '../assets/lottie/halloween-bat.json';
import ghostAnimation from '../assets/lottie/halloween-ghost.json';
import pumpkinAnimation from '../assets/lottie/halloween-pumpkin.json';
import ThemeSpring from './ThemeSpring';

// Efeito de partículas do tema: neve, confete, balões, halloween ou primavera
// contained=true renderiza dentro do container pai (preview do admin)
const CONFETTI_COLORS = ['#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#a855f7', '#facc15'];
const BALLOON_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'];

const rand = (min, max) => min + Math.random() * (max - min);

export const ThemeParticles = ({ type, contained = false, count }) => {
  const items = useMemo(() => {
    if (!type || type === 'primavera') return [];
    if (type === 'halloween') {
      const bats = contained ? 1 : 2;
      const ghosts = contained ? 1 : 1;
      const arr = [];
      for (let i = 0; i < bats; i++) {
        arr.push({ id: `b${i}`, kind: 'bat', top: rand(4, 58),
          size: contained ? rand(40, 56) : rand(64, 116),
          duration: rand(12, 22), delay: i * 3 + rand(0, 4),
          reverse: i % 2 === 1, bob: rand(14, 40), speed: rand(0.85, 1.4) });
      }
      for (let i = 0; i < ghosts; i++) {
        arr.push({ id: `g${i}`, kind: 'ghost', left: rand(4, 86),
          size: contained ? rand(48, 60) : rand(70, 120),
          duration: rand(14, 22), delay: i * 5 + rand(0, 6),
          sway: rand(-60, 60), speed: rand(0.8, 1.15) });
      }
      if (!contained) {
        arr.push({ id: 'pk0', kind: 'pumpkin', top: rand(18, 42), size: rand(88, 116),
          duration: rand(26, 34), delay: rand(4, 10), reverse: Math.random() > 0.5,
          bob: rand(18, 34) });
      }
      return arr;
    }
    const n = count || (type === 'baloes' ? 12 : type === 'confete' ? 40 : 35);
    return Array.from({ length: n }, (_, i) => ({
      id: i,
      left: rand(0, 100),
      size: type === 'baloes' ? rand(26, 46) : type === 'confete' ? rand(6, 11) : rand(4, 9),
      duration: type === 'baloes' ? rand(9, 16) : rand(6, 12),
      delay: rand(0, 10),
      sway: rand(-40, 40),
      color: type === 'confete'
        ? CONFETTI_COLORS[i % CONFETTI_COLORS.length]
        : BALLOON_COLORS[i % BALLOON_COLORS.length],
    }));
  }, [type, count, contained]);

  const wrapperClass = contained
    ? 'absolute inset-0 overflow-hidden pointer-events-none'
    : 'fixed inset-0 overflow-hidden pointer-events-none';

  if (type === 'primavera') {
    return (
      <div className={wrapperClass} data-testid="theme-particles-primavera"
           style={{ zIndex: contained ? 1 : 5, perspective: contained ? 700 : 1300 }}>
        <ThemeSpring contained={contained} count={count} />
      </div>
    );
  }

  if (!type || items.length === 0) return null;

  const moonSize = contained ? 56 : 'clamp(80px, 10vw, 150px)';

  return (
    <div className={wrapperClass} style={{ zIndex: contained ? 1 : 5 }} data-testid={`theme-particles-${type}`}>
      <style>{`
        @keyframes tp-fall {
          0% { transform: translateY(-8vh) translateX(0) rotate(0deg); }
          100% { transform: translateY(108vh) translateX(var(--sway)) rotate(540deg); }
        }
        @keyframes tp-rise {
          0% { transform: translateY(108vh) translateX(0) rotate(-3deg); }
          50% { transform: translateY(50vh) translateX(var(--sway)) rotate(4deg); }
          100% { transform: translateY(-15vh) translateX(0) rotate(-3deg); }
        }
        @keyframes tp-fly-right {
          0% { transform: translateX(-14vw) translateY(0); }
          25% { transform: translateX(16vw) translateY(calc(var(--bob) * -1)); }
          50% { transform: translateX(48vw) translateY(var(--bob)); }
          75% { transform: translateX(80vw) translateY(calc(var(--bob) * -0.6)); }
          100% { transform: translateX(114vw) translateY(0); }
        }
        @keyframes tp-fly-left {
          0% { transform: translateX(114vw) translateY(0); }
          25% { transform: translateX(80vw) translateY(var(--bob)); }
          50% { transform: translateX(48vw) translateY(calc(var(--bob) * -1)); }
          75% { transform: translateX(16vw) translateY(calc(var(--bob) * 0.6)); }
          100% { transform: translateX(-14vw) translateY(0); }
        }
        @keyframes tp-ghost-rise {
          0% { transform: translateY(105vh) translateX(0); opacity: 0; }
          10% { opacity: 0.92; }
          50% { transform: translateY(45vh) translateX(var(--sway)); opacity: 0.9; }
          90% { opacity: 0.7; }
          100% { transform: translateY(-25vh) translateX(0); opacity: 0; }
        }
        @keyframes tp-moon-glow {
          0%, 100% { box-shadow: 0 0 34px 10px rgba(255,222,150,0.45), 0 0 90px 28px rgba(255,200,120,0.20); }
          50% { box-shadow: 0 0 50px 16px rgba(255,228,165,0.65), 0 0 120px 40px rgba(255,205,125,0.30); }
        }
        @keyframes tp-fog-drift {
          0% { transform: translateX(-18%); }
          50% { transform: translateX(14%); }
          100% { transform: translateX(-18%); }
        }
      `}</style>
      {type === 'halloween' && (
        <>
          {/* Lua cheia (CSS puro) com brilho pulsante */}
          <div data-testid="hw-moon" style={{ position: 'absolute',
            top: contained ? '5%' : '4%', right: contained ? '5%' : '5%',
            width: moonSize, height: moonSize, borderRadius: '50%',
            background: `radial-gradient(circle at 62% 58%, rgba(213,186,124,0.5) 0 6%, transparent 7%),
              radial-gradient(circle at 42% 70%, rgba(213,186,124,0.42) 0 4.5%, transparent 5.5%),
              radial-gradient(circle at 56% 32%, rgba(213,186,124,0.36) 0 3.5%, transparent 4.5%),
              radial-gradient(circle at 30% 74%, rgba(213,186,124,0.3) 0 3%, transparent 4%),
              radial-gradient(circle at 38% 35%, #fffbe9 0%, #ffedb8 48%, #f1d689 100%)`,
            animation: 'tp-moon-glow 6s ease-in-out infinite' }} />
          {/* Névoa rasteira */}
          {[0, 1, 2].map((i) => (
            <div key={`fog${i}`} style={{ position: 'absolute',
              bottom: `${-4 + i * 3}%`, left: `${i * 28 - 12}%`,
              width: '68%', height: contained ? '26%' : '22%',
              background: 'radial-gradient(ellipse at center, rgba(210,200,235,0.30) 0%, rgba(210,200,235,0.12) 45%, transparent 72%)',
              filter: 'blur(14px)',
              animation: `tp-fog-drift ${16 + i * 7}s ease-in-out ${i * 3}s infinite` }} />
          ))}
        </>
      )}
      {items.map((p) => {
        if (type === 'halloween') {
          if (p.kind === 'bat') {
            return (
              <div key={p.id} style={{ position: 'absolute', left: 0, top: `${p.top}%`,
                '--bob': `${p.bob}px`,
                animation: `${p.reverse ? 'tp-fly-left' : 'tp-fly-right'} ${p.duration}s linear ${p.delay}s infinite` }}>
                <Lottie src={batAnimation} loop autoplay
                  style={{ width: p.size, height: p.size,
                    filter: 'drop-shadow(0 0 6px rgba(190,170,255,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
              </div>
            );
          }
          if (p.kind === 'pumpkin') {
            return (
              <div key={p.id} style={{ position: 'absolute', left: 0, top: `${p.top}%`,
                '--bob': `${p.bob}px`,
                animation: `${p.reverse ? 'tp-fly-left' : 'tp-fly-right'} ${p.duration}s linear ${p.delay}s infinite` }}>
                <Lottie src={pumpkinAnimation} loop autoplay
                  style={{ width: p.size, height: p.size,
                    filter: 'drop-shadow(0 0 10px rgba(255,150,40,0.5)) drop-shadow(0 3px 6px rgba(0,0,0,0.4))' }} />
              </div>
            );
          }
          // fantasma (boo)
          return (
            <div key={p.id} style={{ position: 'absolute', left: `${p.left}%`, top: 0,
              '--sway': `${p.sway}px`, opacity: 0,
              animation: `tp-ghost-rise ${p.duration}s linear ${p.delay}s infinite` }}>
              <Lottie src={ghostAnimation} loop autoplay
                style={{ width: p.size, height: p.size,
                  filter: 'drop-shadow(0 4px 12px rgba(120,130,200,0.4))' }} />
            </div>
          );
        }
        const common = {
          position: 'absolute',
          left: `${p.left}%`,
          top: 0,
          '--sway': `${p.sway}px`,
          animation: `${type === 'baloes' ? 'tp-rise' : 'tp-fall'} ${p.duration}s linear ${p.delay}s infinite`,
        };
        if (type === 'neve') {
          return <div key={p.id} style={{ ...common, width: p.size, height: p.size,
            borderRadius: '50%', background: 'rgba(255,255,255,0.95)',
            boxShadow: '0 0 6px rgba(255,255,255,0.9)' }} />;
        }
        if (type === 'confete') {
          return <div key={p.id} style={{ ...common, width: p.size, height: p.size * 0.45,
            background: p.color, borderRadius: 1.5 }} />;
        }
        // balões
        return (
          <div key={p.id} style={{ ...common, width: p.size, height: p.size * 1.2 }}>
            <div style={{ width: '100%', height: '100%', background: p.color,
              borderRadius: '50% 50% 48% 52% / 45% 45% 55% 55%',
              boxShadow: 'inset -4px -6px 8px rgba(0,0,0,0.15), inset 3px 4px 6px rgba(255,255,255,0.35)' }} />
            <div style={{ width: 1.5, height: p.size * 0.9, background: 'rgba(120,120,120,0.55)',
              margin: '0 auto' }} />
          </div>
        );
      })}
    </div>
  );
};

export default ThemeParticles;
